import type { FC } from 'react'
import { useRef, useState } from 'react'

import type { ComboboxRef } from '@skytech/ui'
import { MessagePopup, Wizard, WizardStep } from '@skytech/ui'
import { Container } from '@skytech/ui/Container'
import { MAX_DECIMAL_12_4, MIN_PRICE } from '@skytech/utils'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { FormikProps, FormikState } from 'formik'
import { Form, Formik } from 'formik'
import { useNavigate } from 'react-router-dom'

import { useAppContext } from '@/app/index.context'
import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { ADMIN_ROLES, USER_ROLE, useHasRoleBasedAccess } from '@/hooks/access'
import useUser from '@/hooks/user'
import queries from '@/modules/admin/queries'
import type { Policy } from '@/resources/ecom-policy'
import { commonEcomPolicyResources } from '@/resources/ecom-policy'
import { commonQueries } from '@/resources/queries'
import { APP_ROUTES } from '@/util/app-routes'

import { Hardware } from './Hardware'
import { Status } from './Status'
import { UserInfo } from './UserInfo'

import adminResources from '../../resources'
import type { Customer } from '../../resources'
import type { AddAssetPayload } from '../../resources/assets/addAsset'

type FormFields = ReturnType<typeof getInitialValues>

export const NewAsset: FC = () => {
  const { setIsWizardMode } = useAppContext()
  const formRef = useRef<FormikProps<FormFields> | null>(null)
  const comboboxMethodsRef = useRef<ComboboxRef>(null)

  const translate = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const user = useUser()
  const isCustomerAdmin = user?.role === 'customer_admin'
  const [currentStep, setCurrentStep] = useState(0)
  const hasLocationsAccess = useHasRoleBasedAccess([
    ...ADMIN_ROLES,
    USER_ROLE.IT_USER,
    USER_ROLE.IT_PURCHASER,
  ])

  const { data: customers } = useQuery(queries.adminCustomers(), adminResources.getCustomers)
  const { data: employees } = useQuery(queries.adminEmployeeList(hasLocationsAccess), () =>
    adminResources.getEmployees(hasLocationsAccess)
  )

  const { data: ecomPolicies } = useQuery(
    commonQueries.policyList(),
    commonEcomPolicyResources.getList
  )

  /* The list of available policies need to be filtered by the the selected customer */
  const activeEcomPoliciesForSelectedCustomer =
    ecomPolicies?.filter(policy => {
      const selectedCustomerId = customers?.find(
        customer => `${customer.name} ${customer.orgNo}` === formRef.current?.values.customerName
      )?.id
      const isLocalPolicyMatchingAssetCustomer = policy.customerId === selectedCustomerId
      const isGlobalPolicy = Boolean(policy.customerHeadName)
      const isPolicyAvailableForSelectedCustomer =
        isGlobalPolicy || isLocalPolicyMatchingAssetCustomer
      const isPolicyActive = policy.isActive

      return isPolicyActive && isPolicyAvailableForSelectedCustomer
    }) || []
  const [saveError, setSaveError] = useState('')
  const addAssetMutation = useMutation(adminResources.addAsset, {
    onError(error: { text: string; response: Response; json: Object }) {
      setSaveError(error.response.statusText)
    },
    onSuccess(res) {
      navigate(APP_ROUTES.HARDWARE_ASSET.replace(':id', String(res?.id)))
    },
    onSettled() {
      queryClient.invalidateQueries(queries.adminAssetList({ allAssets: true }), {
        refetchType: 'all',
      })
    },
  })

  return (
    <Container onSwipedLeft={() => navigate(APP_ROUTES.HARDWARE_ASSETS)}>
      <Formik<FormFields>
        initialValues={getInitialValues()}
        enableReinitialize
        innerRef={formRef}
        validateOnBlur={false}
        validate={values => {
          const errors: Partial<Record<keyof FormFields, string>> = {}

          if (!isCustomerAdmin) {
            if (!values.customerName) {
              errors.customerName = translate('This field is mandatory')
            }
            if (
              !customers?.find(
                customer => `${customer.name} ${customer.orgNo}` === values.customerName
              )
            ) {
              errors.customerName = translate('Please choose one of the available companies')
            }
          }
          if (!values.cost) {
            errors.cost = translate('This field is mandatory')
          }
          if (values.cost && Number(values.cost) > MAX_DECIMAL_12_4) {
            errors.cost = translate('The cost is too big')
          }
          if (values.cost && Number(values.cost) < MIN_PRICE) {
            errors.cost = translate('The cost is too small')
          }
          if (!values.assetDescription) {
            errors.assetDescription = translate('This field is mandatory')
          }

          return errors
        }}
        onSubmit={({ customerName, ...formValues }) => {
          if (!formRef.current?.dirty) return

          let customerId = customers?.find(
            customer => `${customer.name} ${customer.orgNo}` === customerName
          )?.id

          if (isCustomerAdmin) customerId = user.customerId
          if (!customerId) throw Error('Customer id missing')
          // We display username and phone number but send only the username
          const parsedUserName = formValues.userName.split(' -')[0]

          const payload: AddAssetPayload = {
            ...formValues,
            customerId,
            costObjectId: employees?.find(
              employee => employee.name?.toLowerCase() === parsedUserName.toLowerCase()
            )?.id,
            userName: parsedUserName,
            cost: Number(formValues.cost),
            userTypeId: Number(formValues.userTypeId),
            statusId: Number(formValues.statusId),
            customerAddressId: Number(formValues.customerAddressId),
            ownershipTypeId: Number(formValues.ownershipTypeId),
            typeId: Number(formValues.typeId),
            ecomPolicyId: formValues.ecomPolicyId ? Number(formValues.ecomPolicyId) : null,
          }
          addAssetMutation.mutate(payload)
        }}
      >
        {({ values }) => {
          const isCurrentTabValid = getIsCurrentTabValid({
            values,
            currentTab: currentStep === 0 ? 'hardware' : currentStep === 1 ? 'user-info' : 'status',
            customers: customers || [],
            ecomPolicies: activeEcomPoliciesForSelectedCustomer,
            isCustomerAdmin: isCustomerAdmin,
          })
          const customerId = customers?.find(
            customer => `${customer.name} ${customer.orgNo}` === values.customerName
          )?.id

          return (
            <Form>
              <Wizard
                skipButtonTextDefault={<Translation id="Skip" />}
                nextButtonTextDefault={<Translation id="Next Step" />}
                previousButtonTextDefault={<Translation id="Previous Step" />}
                isLoading={addAssetMutation.isLoading}
                onIsWizardModeSet={setIsWizardMode}
                onComplete={() => formRef.current?.submitForm()}
                onStepChange={nextStep => setCurrentStep(nextStep)}
              >
                <WizardStep name={<Translation id="Hardware" />} isValid={isCurrentTabValid}>
                  <Hardware
                    isCustomerAdmin={isCustomerAdmin}
                    comboboxMethodsRef={comboboxMethodsRef}
                  />
                </WizardStep>

                <WizardStep name={<Translation id="User info" />} isValid={isCurrentTabValid}>
                  <UserInfo
                    selectedCustomerId={customerId}
                    comboboxMethodsRef={comboboxMethodsRef}
                  />
                </WizardStep>

                <WizardStep
                  name={<Translation className="capitalize" id="Status" />}
                  isValid={isCurrentTabValid}
                  nextButtonText={<Translation id="Complete" />}
                >
                  <Status ecomPolicies={activeEcomPoliciesForSelectedCustomer} />
                </WizardStep>
              </Wizard>
            </Form>
          )
        }}
      </Formik>

      <MessagePopup
        title={<Translation id="Saving form error" />}
        message={
          <>
            <Translation id="We encountered a problem while submitting the form. Please refresh and try again." />{' '}
            <Translation id="Error" />: {saveError}
          </>
        }
        isOpen={Boolean(saveError)}
        onClose={() => setSaveError('')}
      />
    </Container>
  )
}

export function getIsCurrentTabValid({
  values,
  currentTab,
  customers,
  ecomPolicies,
  isCustomerAdmin,
}: {
  currentTab: string
  values: FormikState<FormFields>['values'] | undefined
  customers: Customer[]
  ecomPolicies: Policy[] | undefined
  isCustomerAdmin?: boolean
}): boolean {
  if (!values) return false
  switch (currentTab) {
    case 'hardware': {
      let customerExists = false

      if (!isCustomerAdmin && customers) {
        customerExists = Boolean(
          customers.find(customer => `${customer.name} ${customer.orgNo}` === values.customerName)
        )
      }
      const hasValidCustomer = isCustomerAdmin || customerExists
      const isValid = Boolean(
        values.assetDescription && values.cost && values.typeId && hasValidCustomer
      )
      return isValid
    }
    case 'user-info': {
      const hasUserName = Boolean(values.userName)
      const hasUserTypeId = Boolean(values.userTypeId)
      const hasReceived = Boolean(values.received)

      const isValid = hasUserName && hasUserTypeId && hasReceived
      return isValid
    }
    case 'status': {
      // Only required if there are any available
      const isEcomPolicyValid =
        ecomPolicies && ecomPolicies.length > 0 ? Boolean(values.ecomPolicyId) : true
      const isValid = Boolean(
        values.statusId && values.ownershipTypeId !== null && isEcomPolicyValid
      )
      return isValid
    }
    default:
      return true
  }
}

function getInitialValues() {
  return {
    userName: '',
    customerAddressId: '',
    comment: '',
    cost: '',
    customerName: '',
    assetDescription: '',
    imeiSnr: '',
    statusId: '',
    typeId: '',
    userTypeId: '',
    ownershipTypeId: '',
    ecomPolicyId: '',
    received: '',
    imeiNumber: '',
    serialNumber: '',
  }
}
